import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface SeismicHazardPopupProps {
  lat: number;
  lon: number;
  hazard: number;
  hazard_prev: number | null;
  hazard_next: number | null;
  interpolated_coords?: ({ intensity: number; probability: number } | { ash_load: number; probability: number }) | null;
  prev_coords?: ({ intensity: number; probability: number } | { ash_load: number; probability: number }) | null;
  next_coords?: ({ intensity: number; probability: number } | { ash_load: number; probability: number }) | null;
  hazard_curve: {
    intensities: number[];
    probabilities: number[];
  };
  all_percentiles_curves?: {
    [percentile: number]: {
      intensities: number[];
      probabilities: number[];
    };
  };
  onDownload?: (hazardCurveData: any, lat: number, lon: number, all_percentiles_curves?: any) => void;
}

// Helper function per ottenere il valore X dalle coordinate
const getXValue = (coords: { intensity: number; probability: number } | { ash_load: number; probability: number }): number => {
  return 'intensity' in coords ? coords.intensity : coords.ash_load;
};

const SeismicHazardPopup: React.FC<SeismicHazardPopupProps> = ({ 
  lat, 
  lon, 
  hazard, 
  hazard_prev, 
  hazard_next, 
  interpolated_coords, 
  prev_coords, 
  next_coords, 
  hazard_curve, 
  all_percentiles_curves, 
  onDownload 
}) => {
  // Colori per i diversi percentili
  const percentileColors = [
    'rgb(255, 99, 132)',   // Rosso
    'rgb(54, 162, 235)',   // Blu
    'rgb(255, 205, 86)',   // Giallo
    'rgb(75, 192, 192)',   // Verde acqua
    'rgb(153, 102, 255)',  // Viola
    'rgb(255, 159, 64)',   // Arancione
    'rgb(199, 199, 199)',  // Grigio
    'rgb(83, 102, 255)',   // Blu scuro
    'rgb(255, 99, 255)',   // Magenta
    'rgb(99, 255, 132)'    // Verde chiaro
  ];

  let datasets: any[] = [];
  
  // Ottieni i dati della curva di riferimento
  const mainCurveData = all_percentiles_curves && Object.keys(all_percentiles_curves).length > 0 
    ? all_percentiles_curves[parseInt(Object.keys(all_percentiles_curves)[0])]
    : hazard_curve;
    
  const intensities = Array.isArray(mainCurveData.intensities) ? mainCurveData.intensities : [];
  const probabilities = Array.isArray(mainCurveData.probabilities) ? mainCurveData.probabilities : [];
  
  // Ordina i dati per intensity
  const sortedIndices = intensities
    .map((_, i) => i)
    .sort((a, b) => intensities[a] - intensities[b]);
  
  const sortedData = sortedIndices.map(i => ({
    x: intensities[i],
    y: probabilities[i]
  }));

  // Curva principale (linea blu con cerchi come matplotlib)
  datasets = [{
    label: 'Hazard Curve',
    data: sortedData,
    borderColor: '#1f77b4',
    backgroundColor: '#1f77b4',
    pointBackgroundColor: '#1f77b4',
    pointBorderColor: '#1f77b4',
    pointRadius: 2,
    pointHoverRadius: 4,
    borderWidth: 1,
    fill: false,
    tension: 0,
    showLine: true
  }];
  
  // Aggiungi punti speciali sovrapposti al grafico
  if (prev_coords && next_coords) {
    // Upper Point (verde) - sovrapposto al grafico
    datasets.push({
      label: 'Min Seismic Hazard Intensity',
      data: [{ x: getXValue(next_coords), y: next_coords.probability }],
      backgroundColor: 'green',
      borderColor: 'green',
      pointRadius: 6,
      pointHoverRadius: 8,
      showLine: false,
      pointStyle: 'circle',
      borderWidth: 1
    });

    // Lower Point (blu) - sovrapposto al grafico
    datasets.push({
      label: 'Max Seismic Hazard Intensity',
      data: [{ x: getXValue(prev_coords), y: prev_coords.probability }],
      backgroundColor: 'blue',
      borderColor: 'blue',
      pointRadius: 6,
      pointHoverRadius: 8,
      showLine: false,
      pointStyle: 'circle',
      borderWidth: 1
    });
  }

  if (interpolated_coords) {
    // Interpolated Point (rosso X) - sovrapposto al grafico
    datasets.push({
      label: 'Interpolated Point', 
      data: [{ x: getXValue(interpolated_coords), y: interpolated_coords.probability }],
      backgroundColor: 'red',
      borderColor: 'red',
      pointRadius: 5,
      pointHoverRadius: 7,
      pointStyle: 'crossRot',
      showLine: false,
      borderWidth: 1
    });
    
    // Linea orizzontale threshold (grigia tratteggiata)
    datasets.push({
      label: 'Threshold Probability',
      data: [
        { x: Math.min(...intensities), y: interpolated_coords.probability },
        { x: Math.max(...intensities), y: interpolated_coords.probability }
      ],
      borderColor: 'gray',
      backgroundColor: 'gray',
      borderDash: [5, 5],
      pointRadius: 0,
      fill: false,
      showLine: true,
      tension: 0
    });
  }

  // Aggiungi punti speciali se disponibili
  const specialPoints = [];
  






  const data = {
    datasets: datasets
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          font: { size: 11 }
        }
      },
      title: {
        display: true,
        text: 'Hazard Curve with Interpolated Point',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.dataset.label || '';
            const intensity = context.parsed.x;
            const probability = context.parsed.y;
            return `${label}: Intensity ${intensity.toFixed(2)}, P = ${probability.toFixed(6)}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'Intensity',
          font: { size: 12 }
        },
        min: Math.min(...intensities) - 0.5,
        max: Math.max(...intensities) + 0.5,
        grid: {
          display: true,
          color: 'rgba(128, 128, 128, 0.3)'
        },
        ticks: {
          stepSize: 1,
          font: { size: 10 }
        }
      },
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Exceedance Probability',
          font: { size: 12 }
        },
        reverse: false, // Inversione asse Y - parte da 0 in alto
        min: 0,
        max: Math.max(...probabilities) || 1,
        grid: {
          display: true,
          color: 'rgba(128, 128, 128, 0.3)'
        },
        ticks: {
          font: { size: 10 },
          stepSize: 0.1, // Tick ogni 0.1 per maggiore dettaglio
          callback: function(value: any) {
            // Mostra valori ogni 0.1: 0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0
            return value.toFixed(1);
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6
      },
      line: {
        tension: 0
      }
    },

  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(hazard_curve, lat, lon, all_percentiles_curves);
    }
  };

  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif', 
      width: '600px', 
      height: '520px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ 
        padding: '10px',
        borderBottom: '1px solid #eee',
        backgroundColor: '#f8f9fa'
      }}>
        <h3 style={{ 
          margin: '0 0 10px 0', 
          fontSize: '16px',
          color: '#333'
        }}>
          Seismic Hazard Analysis
        </h3>
        <div style={{ fontSize: '13px', color: '#666' }}>
          <div><strong>Location:</strong> {lat.toFixed(6)}°N, {lon.toFixed(6)}°E</div>
          <div style={{ marginTop: '5px' }}>
            <strong>Computed Seismic Hazard Intensity:</strong> {hazard.toFixed(2)}
            {hazard_prev !== null && hazard_next !== null && (
              <span style={{ fontSize: '11px', color: '#888' }}>
                {' '}(interpolated between {hazard_next.toFixed(2)} and {hazard_prev.toFixed(2)})
              </span>
            )}
          </div>
          
          {interpolated_coords && (
            <div style={{ marginTop: '5px', fontSize: '11px', color: '#555' }}>
              <strong>Computed Seismic Hazard Intensity:</strong> Intensity = {getXValue(interpolated_coords).toFixed(3)}, 
              Probability = {interpolated_coords.probability.toFixed(6)}
            </div>
          )}
          
          {prev_coords && (
            <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>
              <strong>Max Seismic Hazard Intensity:</strong> Intensity = {getXValue(prev_coords).toFixed(2)}, 
              Probability = {prev_coords.probability.toFixed(6)}
            </div>
          )}
          
          {next_coords && (
            <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>
              <strong>Min Seismic Hazard Intensity:</strong> Intensity = {getXValue(next_coords).toFixed(2)}, 
              Probability = {next_coords.probability.toFixed(6)}
            </div>
          )}
        </div>
      </div>
      
      <div style={{ 
        flex: 1, 
        padding: '15px',
        position: 'relative'
      }}>
        <Line data={data} options={options} />
      </div>
      
      <div style={{ 
        padding: '10px',
        borderTop: '1px solid #eee',
        backgroundColor: '#f8f9fa',
        textAlign: 'center'
      }}>
        <button 
          onClick={handleDownload}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Download Hazard Curve Data
        </button>
      </div>
    </div>
  );
};

export default SeismicHazardPopup;